import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Button } from "@/components/ui/button";
import { Activity, ArrowRight } from "lucide-react";

const HERO =
  "/winx.jpg";

export default function Login() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState(new URLSearchParams(window.location.search).get("mode") === "register" ? "register" : "login");
  const [form, setForm] = useState({ email: "", password: "", name: "", accept_terms: false });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await (mode === "login" ? login(form) : register(form));
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.2fr_1fr] noise">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={HERO} alt="Winx winning at Randwick" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#080C14]/40 via-[#080C14]/60 to-[#080C14]" />
        <div className="absolute bottom-12 left-12 max-w-lg fade-up">
          <div className="eyebrow text-emerald-400 mb-3">Selection • 6 Matrices • $ Price</div>
          <h1 className="text-5xl lg:text-6xl font-bold leading-[0.95]">
            Rate the field.<br />Find the value.
          </h1>
          <p className="mt-4 text-slate-300 text-base">
            Filter races with your rules, score every runner with your rating rules and convert ratings into a % chance and fair $ price.
          </p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 md:p-12 grid-bg">
        <form onSubmit={submit} className="w-full max-w-sm fade-up" data-testid="auth-form">
          <div className="flex items-center gap-2 mb-10">
            <span className="h-8 w-8 grid place-items-center rounded bg-emerald-500 text-[#080C14]">
              <Activity className="h-4 w-4" strokeWidth={2.5} />
            </span>
            <span className="font-display text-2xl font-bold">STRATEGIC<span className="text-emerald-400">/</span>RATINGS</span>
          </div>
          <h2 className="text-3xl font-bold mb-1">{mode === "login" ? "Sign in" : "Create account"}</h2>
          <p className="text-sm text-slate-400 mb-8">
            {mode === "login" ? "Access your selections and ratings." : "Start rating races in minutes."}
          </p>
          <div className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="eyebrow">Name</label>
                <Input data-testid="register-name-input" value={form.name} onChange={set("name")} className="mt-1.5 bg-[#0F172A]" />
              </div>
            )}
            <div>
              <label className="eyebrow">Email</label>
              <Input data-testid="login-email-input" type="email" required value={form.email} onChange={set("email")} className="mt-1.5 bg-[#0F172A]" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="eyebrow">Password</label>
                {mode === "login" && <Link to="/forgot-password" className="text-xs text-emerald-400 hover:text-emerald-300" data-testid="forgot-password-link">Forgot password?</Link>}
              </div>
              <div className="mt-1.5"><PasswordInput data-testid="login-password-input" required minLength={6} value={form.password} onChange={set("password")} className="bg-[#0F172A]" /></div>
            </div>
            {mode === "register" && (
              <label className="flex items-start gap-2.5 text-sm text-slate-300 cursor-pointer" data-testid="register-terms">
                <input type="checkbox" required checked={form.accept_terms} onChange={(e) => setForm({ ...form, accept_terms: e.target.checked })}
                  className="mt-0.5 h-4 w-4 accent-emerald-500" data-testid="register-terms-checkbox" />
                <span>I am 18+ and agree to the <Link to="/terms" target="_blank" className="text-emerald-400 hover:text-emerald-300 underline" data-testid="register-terms-link">Terms of Use</Link></span>
              </label>
            )}
            {error && (
              <div data-testid="auth-error" className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-3 py-2">
                {error}
              </div>
            )}
            <Button type="submit" disabled={busy || (mode === "register" && !form.accept_terms)} data-testid="login-submit-button" className="w-full h-11 font-semibold group">
              {busy ? "Please wait..." : mode === "login" ? "Sign in" : "Create account"}
              <ArrowRight className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>
          <button
            type="button"
            data-testid="auth-mode-toggle"
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
            className="mt-6 text-sm text-slate-400 hover:text-emerald-400 transition-colors"
          >
            {mode === "login" ? "No account? Create one" : "Already have an account? Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
